import { useMemo } from "react";
import { energyStarPropertyType } from "../../../../global_functions/common";
import { useDetailedSiteSummaryQuery } from "../../../../global_functions/sites/sitesQueries";

import { ValueOf } from "type-fest";
import {
  AllSpaceVariants,
  isRentableSpace,
  spaceTypeOptions,
} from "../tenantsAndSpacesUtils";

const PAGE_VARIANTS = {
  OFFICE: "office",
  MULTIFAMILY: "multifamily",
  ALL_OTHERS: "allOthers",
} as const;
type PageVariant = ValueOf<typeof PAGE_VARIANTS>;

const getPageVariant = (
  propertyType?: number | null
): PageVariant | undefined => {
  if (typeof propertyType !== "number" || isNaN(propertyType)) return;
  if (propertyType === energyStarPropertyType["Multifamily Housing"])
    return PAGE_VARIANTS.MULTIFAMILY;
  if (propertyType === energyStarPropertyType.Office) {
    return PAGE_VARIANTS.OFFICE;
  }
  return PAGE_VARIANTS.ALL_OTHERS;
};

export function useIsPropertyPageEnabled({ siteId }: { siteId?: string } = {}) {
  const { data, isLoading } = useDetailedSiteSummaryQuery({
    siteId,
  });

  return useMemo(() => {
    const pageVariant = getPageVariant(data?.energyStarPropertyType);

    const enabled = typeof pageVariant !== "undefined";
    const isSpacesEnabled =
      !!pageVariant && pageVariant !== PAGE_VARIANTS.ALL_OTHERS;
    const isUnitTypesEnabled = pageVariant === PAGE_VARIANTS.MULTIFAMILY;
    const isOfficeVariant = pageVariant === PAGE_VARIANTS.OFFICE;

    return {
      enabled,
      isSpacesEnabled,
      isUnitTypesEnabled,
      isPropertyUseEnabled: isOfficeVariant,
      isOfficeVariant,
      pageVariant,
      site: data,
      isLoading,
    };
  }, [data, isLoading]);
}

export const isOfficeSpace = (space: Partial<AllSpaceVariants>) =>
  isRentableSpace(space) && space.spaceType !== "MultiFamily";
export const isMultifamilySpace = (space: Partial<AllSpaceVariants>) =>
  space.spaceType === "MultiFamily";
export const isCommonAreaOrPoolSpace = (space: Partial<AllSpaceVariants>) =>
  space.spaceType === "CommonArea" || space.spaceType === "Pool";

export const isParkingSpace = (space: Partial<AllSpaceVariants>) =>
  space.spaceType === "Parking";

export const roofSpaceTypeOptions = spaceTypeOptions.filter(
  ({ id }) => id === "CommonArea" || id === "Pool"
);
